import React, { useEffect, useState, useContext } from "react";

import Caption from "./Typography/Caption";
import routes from "../setup/routes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { KEYS } from "../setup/entitlements";
import Entitlement, { isAllowed } from "./Entitlements";
import { DispatchContext, StateContext } from "../store/context";
import Logo from "../static/logo.png";
import DarkLogo from "../static/darkLogo.svg";
import { UPDATE_BASE_CURRENCY, UPDATE_USER } from "../store/types";
import { Select, Button } from "antd";
import DarkModeSwitcher from "./darkModeSwithcer";
import PaletteSwitcher from "./paletteSwitcher";
import { usePalette } from "../hooks/usePalette";
import "../styles/glass-tokens.css";
import "./sidebar.theme.css";
import { disconnectChatSocket, socket } from "../config/socketService";
import { saveToSessionStorage } from "../utils/sessionStorageState";
import { terminateUserSession } from "../services/session.service";
import initialState from "../store/state";
import { Menu, LogOut, FileText } from "lucide-react";
import { SIDEBAR_ICON_MAP } from "./sidebarIcons";
import ROUTES from "../setup/routes";

type SidebarItem = {
  label: string;
  icon: string;
  path: string;
  entitlements: string[];
  adminOnly?: boolean;
  allowAdminWithoutEntitlement?: boolean;
  requiresAuth?: boolean;
};

const normalizePath = (value: string = "") => {
  const withLeadingSlash = value.startsWith("/") ? value : `/${value}`;
  const normalized = withLeadingSlash.replace(/\/+/g, "/").replace(/\/$/, "");
  return normalized || "/";
};

const matchesMenuPath = (menuPath: string, currentPath: string) => {
  const normalizedMenuPath = normalizePath(menuPath);
  const normalizedCurrentPath = normalizePath(currentPath);
  if (normalizedMenuPath === "/") {
    return normalizedCurrentPath === "/";
  }
  return (
    normalizedCurrentPath === normalizedMenuPath ||
    normalizedCurrentPath.startsWith(`${normalizedMenuPath}/`)
  );
};

const MobileSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("Dashboard");

  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("darkMode");
    return savedTheme === "true" || false;
  });

  const { authUser, baseCurrency } = useContext(StateContext);

  const allowed = authUser?.allowed || [];
  const username = authUser?.username || "";
  const isAdminUserOne =
    [1, 2, 3].includes(Number(authUser?.id || authUser?.userId || 0)) ||
    authUser?.role_name === "admin";
  const isAuthenticated =
    !!authUser?.token || !!String(localStorage.getItem("token") || "").trim();

  const dispatch = useContext(DispatchContext);

  const items: SidebarItem[] = [
    {
      label: "Beginner Analysis",
      icon: "BeginnerAnalysis",
      path: `${routes.DASHBOARD}/${routes.BEGINNER}`,
      entitlements: [KEYS.BEGINNER_ANALYSIS_VIEW],
    },
    {
      label: "Detailed Analysis",
      icon: "SingleStockAnalysis",
      path: `${routes.DASHBOARD}/${routes.SINGLE_STOCK_ANALYSIS}`,
      entitlements: [KEYS.SINGLE_STOCK_ANALYSIS_VIEW],
    },
    {
      label: "Compare",
      icon: "fundemantal",
      path: `${routes.DASHBOARD}/${routes.OVERVIEW}`,
      entitlements: [KEYS.COMPARE],
    },
    {
      label: "Dynamic Graphs",
      icon: "bar",
      path: `${routes.DASHBOARD}/${routes.DYNAMIC_GRAPHS}`,
      entitlements: [KEYS.VIEW_DYNAMIC_GRAPH],
    },
    {
      label: "Financials",
      icon: "Dollar",
      path: `${routes.DASHBOARD}/${routes.FINANCIALS}`,
      entitlements: [KEYS.FINANCIAL_VIEW],
    },
    {
      label: "Screener",
      icon: "clipboard",
      path: `${routes.DASHBOARD}/${routes.SCREENER}`,
      entitlements: [KEYS.STOCK_SCREENER_VIEW],
    },
    {
      label: "Market Heatmap",
      icon: "Heatmap",
      path: `${routes.DASHBOARD}/${routes.MARKET_HEATMAP}`,
      entitlements: [KEYS.MARKET_HEATMAP_VIEW],
    },
    {
      label: "Watchlist",
      icon: "Eye",
      path: `${routes.DASHBOARD}/${routes.WATCHLIST}`,
      entitlements: [KEYS.WATCHLIST_VIEW],
    },
    {
      label: "AI Recommendations ",
      icon: "Screen",
      path: `${routes.DASHBOARD}/${routes.AI_ANALYZE}`,
      entitlements: [KEYS.AI_RECOMMENDATION_VIEW],
      requiresAuth: true,
    },
    {
      label: "Stock Assistant",
      icon: "StockAssistant",
      path: `${routes.DASHBOARD}/${routes.STOCK_ASSISTANT}`,
      entitlements: [KEYS.STOCK_ASSISTANT_VIEW],
      requiresAuth: true,
    },
    {
      label: "Support",
      icon: "Support",
      path: `${routes.DASHBOARD}/${routes.SUPPORT}`,
      entitlements: [KEYS.SUPPORT_VIEW],
    },
    {
      label: "Stock Chatbot",
      icon: "ChatBubble",
      path: `${routes.DASHBOARD}/${routes.STOCK_CHATBOT}`,
      entitlements: [KEYS.STOCK_CHATBOT_VIEW],
    },
    {
      label: "Technical Analysis",
      icon: "TechnicalAnalysis",
      path: `${routes.DASHBOARD}/${routes.TECHNICAL_ANALYSIS}`,
      entitlements: [KEYS.TECHNICAL_ANALYSIS_VIEW],
    },
    {
      label: "Falcon Data Entry",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_DATA_ENTRY}`,
      entitlements: [KEYS.FALCON_DATA_ENTRY_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Falcon Extraction",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_EXTRACTION}`,
      entitlements: [KEYS.FALCON_EXTRACTION_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Falcon Senior QC",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_QC}`,
      entitlements: [KEYS.FALCON_SENIOR_QC_VIEW],
      requiresAuth: true,
    },
    {
      label: "Falcon Log Monitor",
      icon: "Pulse",
      path: `${routes.DASHBOARD}/${routes.FALCON_LOG_MONITOR}`,
      entitlements: [KEYS.FALCON_LOG_MONITOR_VIEW],
      allowAdminWithoutEntitlement: true,
    },
    {
      label: "Annual Report Entry",
      icon: "DocumentText",
      path: `${routes.DASHBOARD}/${routes.ANNUAL_REPORT_MANUAL_ENTRY}`,
      entitlements: [KEYS.ANNUAL_REPORT_ENTRY_VIEW],
    },
    {
      label: "Graph Editor",
      icon: "Pulse",
      path: `${routes.DASHBOARD}/${routes.GRAPH_EDITOR}`,
      entitlements: [KEYS.GRAPH_EDITOR],
    },
    // {
    //   label: "Technical Analysis",
    //   icon: "Dashboard",
    //   path: `${routes.DASHBOARD}/${routes.TECHNICAL_ANALYSIS}`,
    //   entitlements: [KEYS.COMPARE],
    // },
    {
      label: "User Management",
      icon: "User",
      path: `${routes.USER_MANAGEMENT}/${routes.USERS}`,
      entitlements: [KEYS.USERS_VIEW, KEYS.USER_MANAGEMENT_VIEW],
    },
    {
      label: "Ad Management",
      icon: "DocumentText",
      path: routes.ADS,
      entitlements: [KEYS.AD_MANAGEMENT],
    },
    {
      label: "Equation Builder",
      icon: "NineDots",
      path: routes.EQUATION_BUILDER,
      entitlements: [KEYS.EQUATIONS_VIEW, KEYS.EQUATIONS_ADD],
    },
    {
      label: "Metrics Management",
      icon: "DocumentText",
      path: routes.METRICS_MANAGEMENT,
      entitlements: [KEYS.METRICS_VIEW, KEYS.METRICS_ADD],
    },
    {
      label: "Stock Market",
      icon: "DocumentText",
      path: routes.STOCK_MARKET,
      entitlements: [KEYS.STOCK_MARKET_VIEW],
    },
    {
      label: "Sector",
      icon: "DocumentText",
      path: routes.SECTOR,
      entitlements: [KEYS.SECTOR_VIEW, KEYS.SECTOR_ADD],
    },
    {
      label: "Company Quarter",
      icon: "DocumentText",
      path: routes.COMPANY_QUARTER,
      entitlements: [KEYS.COMPANY_QUARTERS_VIEW, KEYS.COMPANY_QUARTERS_ADD],
    },
    // // {label: 'PE Valuation', icon: 'signal', path: routes.PE_VALUATIONS},
    // // {label: 'Revenues', icon: 'dots-hr', path: routes.REVENUES},
    // // {label: 'Gross Profit', icon: 'list', path: routes.GROSS_PROFITS},
    // // {label: 'Net Profit', icon: 'list', path: routes.NET_PROFITS},
    {
      label: "Stock Listings",
      icon: "DocumentText",
      path: routes.STOCK_LISTINGS,
      entitlements: [KEYS.GET_ALL_STOCKS],
    },

    // { label: "Components", icon: "Ribbon", path: "/components" },
  ];
  const bottomItems = [
    {
      label: "My Profile",
      icon: "User",
      path: `${routes.DASHBOARD}/${routes.CUSTOMER_PROFILE}`,
      entitlements: [KEYS.MY_PROFILE],
    },
    {
      label: "Settings",
      icon: "DocumentText",
      path: routes.SETTINGS,
      entitlements: [KEYS.SETTINGS],
    },
  ];

  const handleMenuSelection = (e: any) => {
    setSelected(e.currentTarget.id);
    setIsOpen(false); // Close menu after selection on mobile
  };

  const hasSidebarAccess = (item: any) => {
    if (item?.requiresAuth && !isAuthenticated) {
      return false;
    }
    if (item?.adminOnly && !isAdminUserOne) {
      return false;
    }
    if (isAllowed(item?.entitlements || [], allowed)) {
      return true;
    }
    if (isAdminUserOne && item?.allowAdminWithoutEntitlement) {
      return true;
    }
    return false;
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (e: any) => {
    dispatch({ type: UPDATE_BASE_CURRENCY, payload: e });
  };

  const goHome = () => {
    // Was window.location.href = HOMEPAGE (a full-page redirect to the
    // public valueit.io marketing site) — the logo now takes a logged-
    // in user back to their own dashboard instead, via the same client-
    // side route the "landed on /" effect below already resolves to the
    // first allowed item.
    navigate(ROUTES.HOME);
    setIsOpen(false);
  };

  const currencyOptions = [
    { label: "INR", value: "INR" },
    { label: "LKR", value: "LKR" },
    { label: "USD", value: "USD" },
    { label: "QAR", value: "QAR" },
    { label: "AED", value: "AED" },
    { label: "SAR", value: "SAR" },
    { label: "KWD", value: "KWD" },
    { label: "SGD", value: "SGD" },
    { label: "SDG", value: "SDG" },
  ];

  useEffect(() => {
    const filteredItems: any = items.filter((item) => hasSidebarAccess(item));
    const normalizedPath = normalizePath(location.pathname);
    const activeItem = filteredItems.find((item: any) =>
      matchesMenuPath(item.path, location.pathname),
    );
    if (activeItem) {
      setSelected(activeItem.label);
      return;
    }

    const stockDetailBasePath = `/${String(routes.STOCKS || "stocks/:id").split("/:")[0]}`;
    const isStockDetailPath =
      normalizedPath === stockDetailBasePath ||
      normalizedPath.startsWith(`${stockDetailBasePath}/`);
    if (isStockDetailPath) {
      const stockListingItem = filteredItems.find(
        (item: any) =>
          normalizePath(item.path) === normalizePath(routes.STOCK_LISTINGS),
      );
      if (stockListingItem) {
        setSelected(stockListingItem.label);
      }
      return;
    }

    const isHomePath = normalizedPath === "/";
    if (isHomePath && filteredItems.length) {
      navigate(filteredItems[0].path);
      setSelected(filteredItems[0].label);
    }
  }, [allowed, isAdminUserOne, location.pathname, navigate]);

  const handleLogout = async () => {
    try {
      const isTerminated: boolean = await terminateUserSession(username);
      if (isTerminated) {
        // if (socket && socket.connected) {
        //   socket.disconnect();
        // }
        localStorage.removeItem("token");
        localStorage.removeItem("is_first_login");
        localStorage.removeItem("is_manually_created");
        localStorage.removeItem("beginner_selected_ticker");
        localStorage.removeItem("financials_selected_ticker");
        localStorage.removeItem("single_stock_analysis_selected_ticker");
        localStorage.removeItem("fundamental_analysis_selection");
        localStorage.removeItem("dynamic_graphs_selected_tickers");
        localStorage.removeItem("dynamic_graphs_selected_parameters");
        disconnectChatSocket();
        saveToSessionStorage({});
        dispatch({ type: UPDATE_USER, payload: initialState.authUser });
        navigate(ROUTES.LOGIN);
      }
    } catch (e: any) {
      console.log("Error", e.message);
    }
  };

  const toggleDarkMode = () => setDarkMode((prevMode) => !prevMode);
  const { palette, togglePalette } = usePalette();

  const [defaultCurrency, setDefaultCurrency] = useState(baseCurrency);

  const handleLogIn = () => {
    navigate(ROUTES.LOGIN);
  };

  useEffect(() => {
    setDefaultCurrency(baseCurrency);
  }, [baseCurrency]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  return (
    <div className="relative">
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMenu}
        className="lg:hidden fixed top-7 right-4 z-50 p-2 rounded-md bg-[color:var(--vi-card-bg)] border border-[color:var(--vi-card-border)] shadow-md"
      >
        <Menu className="w-4 h-4 text-[color:var(--vi-text-tertiary)]" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed  top-0 left-0 h-full w-64 bg-[color:var(--vi-bg-page)] border-r border-[color:var(--vi-card-border)] transform transition-transform duration-300 ease-in-out z-50
          ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo — shrink-0 so it stays fixed while the menu list below
              scrolls independently. */}
          <div className="shrink-0 px-4 pt-5 pb-2">
            {/* Source SVG viewBox is 66x26 (a wide wordmark) — sized by
                width with h-auto so it scales proportionally instead of
                being stretched into a fixed box. */}
            <img
              src={darkMode ? DarkLogo : Logo}
              className="w-[140px] h-auto cursor-pointer"
              onClick={goHome}
              alt="Logo"
            />
          </div>

          {/* Menu Items — the only region that scrolls */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4">
            {items.map((item, key) => {
              const ItemIcon = SIDEBAR_ICON_MAP[item.icon] || FileText;
              const isActive = selected === item.label;

              return (
                <Entitlement
                  allowedKeys={
                    isAdminUserOne && item.allowAdminWithoutEntitlement
                      ? undefined
                      : item.entitlements
                  }
                  key={key}
                >
                  {!hasSidebarAccess(item) ? null : (
                    <Link
                      id={item.label}
                      to={item.path}
                      onClick={handleMenuSelection}
                    >
                      <div
                        className={`vi-sidebar-nav-item group flex items-center gap-x-3 pl-[10px] pr-2 py-2 mb-2 rounded-md cursor-pointer border-l-2 transition-colors duration-200 ${
                          isActive ? "vi-sidebar-nav-item-active" : ""
                        }`}
                      >
                        <ItemIcon
                          size={18}
                          strokeWidth={1.75}
                          className={`shrink-0 transition-colors duration-200 ${
                            isActive
                              ? "text-[color:var(--vi-accent)]"
                              : "text-[color:var(--vi-text-tertiary)] group-hover:text-[color:var(--vi-accent)]"
                          }`}
                        />
                        <Caption
                          id={item.label}
                          classes="!text-[14px] !font-normal !font-inter"
                          color={
                            isActive
                              ? "text-[color:var(--vi-accent)]"
                              : "text-[color:var(--vi-text)]"
                          }
                        >
                          {item.label}
                        </Caption>
                      </div>
                    </Link>
                  )}
                </Entitlement>
              );
            })}
          </div>

          {/* Bottom Section — shrink-0 so it stays fixed at the bottom */}
          <div className="shrink-0 px-4 pb-4 pt-2">
            {/* Login Card - Show only when not logged in and not collapsed */}
            {!username && isOpen && (
              <div className="mx-2 mb-4">
                <div className="bg-[color:var(--vi-surface)] border border-[color:var(--vi-card-border)] rounded-lg shadow-sm transition-all duration-300 relative">
                  {/* Close button */}
                  <div className="flex flex-col gap-3 p-3 pt-6">
                    <div>
                      <h3 className="text-sm font-semibold text-[color:var(--vi-text)] mb-1">
                        Ready to unlock all features?
                      </h3>
                      <p className="text-xs text-[color:var(--vi-text-secondary)]">
                        Login to access advanced analytics and exclusive
                        insights
                      </p>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleLogIn}
                      className="!bg-[color:var(--vi-accent)] hover:!opacity-90 !border-none !w-full !h-8"
                    >
                      Login
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Currency Selector */}
            <div className="mb-4">
              <Caption classes="mb-2 text-[color:var(--vi-text-tertiary)] w-10 !font-inter">
                Base currency
              </Caption>
              <div className=" w-full flex items-center gap-1">
                <Select
                  onChange={handleChange}
                  options={currencyOptions}
                  placeholder="Currency"
                  value={defaultCurrency}
                  className="w-full min-w-0 flex-1"
                />
                <div className="flex items-center gap-1 shrink-0">
                  <DarkModeSwitcher
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                  />
                  <PaletteSwitcher
                    palette={palette}
                    togglePalette={togglePalette}
                  />
                </div>
              </div>
            </div>

            {/* Bottom Menu Items */}
            <div className="border-t border-[color:var(--vi-card-border)] pt-4 w-full flex items-center justify-between">
              {bottomItems.map((item, key) => {
                const ItemIcon = SIDEBAR_ICON_MAP[item.icon] || FileText;
                const isActive = selected === item.label;

                return (
                  <Entitlement allowedKeys={item.entitlements} key={key}>
                    <Link
                      id={item.label}
                      to={item.path}
                      onClick={handleMenuSelection}
                    >
                      <div
                        className={`vi-sidebar-nav-item group flex items-center gap-x-3 pl-[10px] pr-2 py-2 mb-2 rounded-md cursor-pointer border-l-2 transition-colors duration-200 ${
                          isActive ? "vi-sidebar-nav-item-active" : ""
                        }`}
                      >
                        <ItemIcon
                          size={18}
                          strokeWidth={1.75}
                          className={`shrink-0 transition-colors duration-200 ${
                            isActive
                              ? "text-[color:var(--vi-accent)]"
                              : "text-[color:var(--vi-text-tertiary)] group-hover:text-[color:var(--vi-accent)]"
                          }`}
                        />
                        <Caption
                          id={item.label}
                          classes="!text-[14px] !font-normal !font-inter"
                          color={
                            isActive
                              ? "text-[color:var(--vi-accent)]"
                              : "text-[color:var(--vi-text)]"
                          }
                        >
                          {item.label}
                        </Caption>
                      </div>
                    </Link>
                  </Entitlement>
                );
              })}

              {username && (
                <LogOut
                  onClick={handleLogout}
                  size={20}
                  className="text-[color:var(--vi-text-tertiary)] w-10 mt-2 cursor-pointer transition-colors duration-200 hover:text-[color:var(--vi-accent)]"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSidebar;
